// The corner window's rules, kept free of the DOM so they can be tested: which layout a window size gets, how far
// the scene zooms in, the sizes behind the layout buttons, and which hunt events become a banner (and a chime).
import { SPECIES } from '../../shared/species.ts';
import { CONFIG } from '../../shared/config.ts';
import { GROUND, AREA } from '../../shared/grounds.ts';
import { unlocksBetween, type Unlock } from '../../shared/unlocks.ts';
import { MAP_W, MAP_H } from '../../shared/worldmap.ts';
import { ratingOf, statWeights } from '../../shared/rating.ts';
import { ivPercent, qualityPercent } from '../../shared/rolls.ts';
import type { CreatureDTO, GameEvent } from '../../shared/protocol.ts';

/**
 * strip: a wide, low band along the bottom (or top) of the screen, like Rusty's Retirement.
 * card: a small box in a corner. column: a tall band down the side of a wide monitor.
 */
export type Shape = 'strip' | 'card' | 'column';

/** The layout for a window of w × h CSS pixels. */
export function shapeOf(w: number, h: number): Shape {
  if (w >= h * 2.6 || (h <= 190 && w >= 520)) return 'strip';
  if (h >= w * 1.3 && h >= 380) return 'column';
  return 'card';
}

/** Map pixels per tile (shared/worldmap.ts tiles are painted 24 px). */
export const TILE = 24;

/** Rows of map tiles the scene shows top to bottom, per layout: enough room round the fight to see what's coming. */
export const ROWS: Record<Shape, number> = { strip: 6, card: 8.5, column: 9 };

/** One zoom step multiplies the scale by this much; the wheel and the − + buttons move a step at a time. */
export const ZOOM_STEP = 1.25;
export const ZOOM_MIN = -3, ZOOM_MAX = 4;

/**
 * Device pixels per map pixel for a scene of w × h device pixels. The scene never shows past the edge of the map,
 * never draws art below its own size, and snaps to a whole number when it is close, so pixels stay even.
 */
export function sceneScale(w: number, h: number, shape: Shape, zoom = 0): number {
  const want = (h / (ROWS[shape] * TILE)) * ZOOM_STEP ** zoom;
  const floor = Math.max(1, w / (MAP_W * TILE), h / (MAP_H * TILE));
  const k = Math.min(8, Math.max(floor, want));
  const r = Math.round(k);
  return Math.abs(k - r) < 0.12 && r >= floor ? r : k;
}

/** The ledger's height under a card's scene, and its width beside a strip's. */
export const BAR_H = 46;
export const sideWidth = (w: number) => Math.round(Math.max(290, Math.min(400, w * 0.26)));

/**
 * The part of a w × h window the scene gets; the ledger takes the rest. A card keeps a bar along the bottom, a
 * strip a panel at its right end, a column the lower part. Battery saver gives the ledger the whole window.
 */
export function sceneRect(shape: Shape, w: number, h: number, saver: boolean): { x: number; y: number; w: number; h: number } | null {
  if (saver) return null;
  if (shape === 'strip') return { x: 0, y: 0, w: Math.max(0, w - sideWidth(w)), h };
  if (shape === 'column') return { x: 0, y: 0, w, h: Math.round(Math.max(150, Math.min(h * 0.46, w * 1.1, h - 230))) };
  return { x: 0, y: 0, w, h: Math.max(0, h - BAR_H) };
}

/**
 * Where battery saver takes the window, from the size it has: a strip stays as it is (the ledger lays out along
 * it); anything else becomes a box sized for the ledger, the way the scene's absence frees the room.
 */
export function saverSize(cur: { w: number; h: number }, shape: Shape, screenH: number): { w: number; h: number } {
  if (shape === 'strip') return { ...cur };
  return { w: Math.round(Math.max(300, Math.min(440, cur.w))), h: Math.round(Math.min(screenH * 0.8, 400)) };
}

/** Inner sizes (CSS px) behind the three layout buttons, for a screen of the given work area. */
export function presetSize(shape: Shape, screenW: number, screenH: number): { w: number; h: number } {
  if (shape === 'strip') return { w: Math.round(Math.min(1480, screenW * 0.78)), h: 158 };
  if (shape === 'column') return { w: 300, h: Math.round(Math.min(720, screenH * 0.72)) };
  return { w: 440, h: 264 };
}

/** What a first open asks for when nothing is remembered: a card in the corner. */
export const FIRST_SIZE = { w: 440, h: 264 };

/** A size remembered from last time, kept to something a screen can show. */
export function sanitizeSize(v: unknown, screenW: number, screenH: number): { w: number; h: number } {
  const o = v as { w?: unknown; h?: unknown } | null;
  const w = Number(o?.w), h = Number(o?.h);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w < 200 || h < 100) return { ...FIRST_SIZE };
  return { w: Math.round(Math.min(w, screenW * 0.8)), h: Math.round(Math.min(h, screenH * 0.8)) };
}

/**
 * A moment worth a banner over the scene. pri orders the queue (higher first); chime is the sound it makes when
 * sound is on. Only Auras chime: the window is for glancing at while working, and a sound has to mean something.
 */
export interface Moment { text: string; pri: number; tone: 'gold' | 'blue' | 'plain'; chime?: 'aura-seen' | 'aura-caught'; ms: number }

/** What the moments need to know: Bestiary catches per species (after this batch), and the wild a uid names. */
export interface MomentContext {
  caught: (species: string) => number;
  wild: (uid: number) => { species: string; aura: boolean } | undefined;
  /** What a level opens as the live switches have it (client/hud.ts opensBetween); absent, the plain unlock table. */
  opens?: (from: number, to: number) => Unlock[];
}

const name = (id: string) => SPECIES[id]?.name ?? 'A creature';

/** The banners a batch of hunt events makes, in the order they happened. */
export function momentsFrom(evs: GameEvent[], ctx: MomentContext): Moment[] {
  const out: Moment[] = [];
  for (const e of evs) {
    switch (e.k) {
      case 'spawn':
        if (e.wild.aura) out.push({ text: `Aura ${name(e.wild.species)} appeared`, pri: 5, tone: 'gold', chime: 'aura-seen', ms: 4200 });
        break;
      case 'catch': {
        const c = e.creature, sp = name(c.species);
        if (c.rolls.aura) out.push({ text: `Aura ${sp} caught`, pri: 6, tone: 'gold', chime: 'aura-caught', ms: 5000 });
        else if (c.rolls.bloodline >= 3) out.push({ text: `Bloodline ${CONFIG.bloodline[c.rolls.bloodline].name} ${sp}`, pri: 4, tone: 'gold', ms: 3600 });
        else if (!e.sold && ctx.caught(c.species) <= 1) out.push({ text: `New species · ${sp}`, pri: 3, tone: 'blue', ms: 3200 });
        break;
      }
      case 'miss': {
        const w = ctx.wild(e.uid);
        if (w?.aura) out.push({ text: `Aura ${name(w.species)} broke free`, pri: 5, tone: 'plain', ms: 4200 });
        break;
      }
      case 'levelup': {
        // What a level opens is the news; a plain level-up is a quick note.
        const opens = (ctx.opens ?? unlocksBetween)(e.level - 1, e.level);
        for (const u of opens) out.push({ text: `${u.name} unlocked`, pri: 3, tone: 'blue', ms: 3600 });
        if (!opens.length) out.push({ text: `Trainer Lv ${e.level.toLocaleString('en-US')}`, pri: 1, tone: 'plain', ms: 2200 });
        break;
      }
      case 'hatch':
        out.push({ text: `Hatched ${name(e.creature.species)}`, pri: 2, tone: e.creature.rolls.aura ? 'gold' : 'blue', ms: 3200 });
        break;
      case 'down':
        if (e.relic) out.push({ text: 'A relic dropped', pri: 2, tone: 'gold', ms: 3000 });
        else if (e.drops.some((d) => d.item.endsWith('token'))) out.push({ text: 'Boss key dropped', pri: 2, tone: 'gold', ms: 3000 });
        break;
      case 'retreat': {
        const to = GROUND[e.to];
        out.push({ text: to ? `Retreated to ${AREA[to.area].name}` : 'Retreated to an easier hunt', pri: 1, tone: 'plain', ms: 3000 });
        break;
      }
      case 'move': {
        const to = GROUND[e.to];
        out.push({ text: to ? `Auto-move · ${name(to.species)}` : 'Auto-move', pri: 1, tone: 'plain', ms: 2600 });
        break;
      }
      // Cuts in on its own "click it" banner (pri 5, client/corner/index.ts).
      case 'golden':
        out.push({ text: `Golden ${name(e.species)} · +${e.coins.toLocaleString('en-US')} coins`, pri: 6, tone: 'gold', ms: 4000 });
        break;
    }
  }
  return out;
}

/** A queue of banners: the most important waiting one shows next, and at most a few wait. */
export class MomentQueue {
  private waiting: Moment[] = [];
  private shown: { m: Moment; until: number } | null = null;
  private max: number;
  constructor(max = 4) { this.max = max; }

  push(list: Moment[]) {
    for (const m of list) {
      // An Aura caught replaces "appeared" for the same wild if that is still on screen or waiting.
      if (m.chime === 'aura-caught') this.waiting = this.waiting.filter((x) => x.chime !== 'aura-seen');
      this.waiting.push(m);
    }
    this.waiting.sort((a, b) => b.pri - a.pri);
    if (this.waiting.length > this.max) this.waiting.length = this.max;
  }

  /** The banner to show at `now`, or null. A more important moment cuts in on a lesser one. */
  current(now: number): Moment | null {
    const top = this.waiting[0];
    if (this.shown && now < this.shown.until && !(top && top.pri > this.shown.m.pri)) return this.shown.m;
    if (!top) { this.shown = null; return null; }
    this.waiting.shift();
    this.shown = { m: top, until: now + top.ms };
    return top;
  }

  clear() { this.waiting = []; this.shown = null; }
}

/** Per-hour rate of v over ms, or null in the first five minutes, when a rate means little (as the session panel). */
export function perHour(v: number, ms: number): number | null {
  return ms >= 5 * 60_000 ? (v * 3_600_000) / ms : null;
}

/** The orders the session's catches can be listed in: the same as the hunt view's Catches tab. */
export const CAP_SORTS = [['recent', 'Recent'], ['grade', 'Grade'], ['talent', 'Talent'], ['bloodline', 'Bloodline'], ['quality', 'Quality'], ['level', 'Level']] as const;
export type CapSort = typeof CAP_SORTS[number][0];
export const isCapSort = (v: unknown): v is CapSort => CAP_SORTS.some(([k]) => k === v);

export interface Caught { c: CreatureDTO; at: number; sold?: number }
/** A catch's rating and Talent, worked out once: rolls never change after a catch. */
const rolled = new Map<number, { rating: number; talent: number }>();
export function rollsOf(c: CreatureDTO) {
  let v = rolled.get(c.id);
  if (!v) {
    if (rolled.size > 600) rolled.clear();
    v = { rating: ratingOf(c.rolls, c.species), talent: ivPercent(c.rolls, statWeights(c.species)) };
    rolled.set(c.id, v);
  }
  return v;
}

/** The session's catches (oldest first, as kept) in the chosen order, best first; ties go to the newer catch. */
export function sortCatches<T extends Caught>(list: readonly T[], sort: CapSort): T[] {
  const rows = list.map((x, i) => ({ x, i, r: rollsOf(x.c) }));
  type Row = typeof rows[number];
  const by: Record<CapSort, (a: Row, b: Row) => number> = {
    recent: () => 0,
    grade: (a, b) => b.r.rating - a.r.rating,
    talent: (a, b) => b.r.talent - a.r.talent,
    bloodline: (a, b) => b.x.c.rolls.bloodline - a.x.c.rolls.bloodline || b.r.rating - a.r.rating,
    quality: (a, b) => qualityPercent(b.x.c.rolls) - qualityPercent(a.x.c.rolls),
    level: (a, b) => b.x.c.level - a.x.c.level,
  };
  return rows.sort((a, b) => by[sort](a, b) || b.i - a.i).map((r) => r.x);
}
